import { FaHome } from "react-icons/fa";

/**
 * A sliding photo track.
 *
 * Every photo sits side by side on one strip; moving between them slides the
 * whole strip. The photo leaving shrinks as it goes and the one arriving grows
 * back to full size, so the movement reads as physical rather than as one image
 * being swapped for another.
 *
 * Rendering the whole strip is what makes that possible — the neighbours have to
 * exist to be animated. They stay cheap: only the active photo is eager, the
 * rest load lazily.
 *
 * `failed` is keyed by photo URL, not position, so a broken image stays broken
 * for that photo wherever it appears rather than blanking whatever happens to
 * sit at the same index in another listing.
 */
/**
 * Stands in for a photo that is missing or broken. Deliberately light — a slab
 * of grey the size of the frame reads as the page failing, where an outlined
 * empty box reads as a listing that simply has no pictures yet. More than half
 * the listings in the system are in that state.
 */
const Placeholder = ({ label, size }) => (
  <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-light_gray bg-white">
    <FaHome color="#c7c9d3" size={size} />
    <span className="text-xs text-gray-400">{label}</span>
  </div>
);

const PhotoCarousel = ({ photos, index, failed, onFailed, emptyLabel }) => {
  if (!photos.length) {
    return (
      <div className="h-full w-full p-1">
        <Placeholder label={emptyLabel} size={56} />
      </div>
    );
  }

  return (
    <div
      className="flex h-full transition-transform duration-450 ease-swift motion-reduce:transition-none"
      style={{ transform: `translateX(-${index * 100}%)` }}
      data-testid="photo-track"
    >
      {photos.map((src, i) => (
        <div key={`${src}-${i}`} className="h-full w-full shrink-0 p-1">
          {failed[src] ? (
            <Placeholder label="Photo unavailable" size={40} />
          ) : (
            <img
              alt={`${i + 1} of ${photos.length}`}
              src={src}
              loading={i === index ? "eager" : "lazy"}
              onError={() => onFailed(src)}
              className={`h-full w-full rounded-md object-cover transition-transform duration-450 ease-swift motion-reduce:transition-none ${
                i === index ? "scale-100" : "scale-[0.85]"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default PhotoCarousel;
